// A small colour picker (no native <input type="color">): a saturation and
// brightness square, a hue bar and a hex field. Opens as a popover next to
// its anchor; onInput fires while dragging, onPick when the colour is set.
//
//   openColorPicker(anchor, '#FF5A1F', { onInput, onPick })

type Hsv = { h: number; s: number; v: number }
type Options = { onInput?: (hex: string) => void; onPick: (hex: string) => void }

export function hexToHsv(hex: string): Hsv {
  const n = parseInt(hex.replace('#', ''), 16)
  const r = ((n >> 16) & 255) / 255
  const g = ((n >> 8) & 255) / 255
  const b = (n & 255) / 255
  const max = Math.max(r, g, b)
  const d = max - Math.min(r, g, b)
  let h = 0
  if (d) h = max === r ? ((g - b) / d) % 6 : max === g ? (b - r) / d + 2 : (r - g) / d + 4
  return { h: (h * 60 + 360) % 360, s: max ? d / max : 0, v: max }
}

export function hsvToHex({ h, s, v }: Hsv): string {
  const f = (k: number) => {
    const n = (k + h / 60) % 6
    return v - v * s * Math.max(0, Math.min(n, 4 - n, 1))
  }
  return `#${[f(5), f(3), f(1)].map((c) => Math.round(c * 255).toString(16).padStart(2, '0')).join('').toUpperCase()}`
}

export const isHex = (value: string) => /^#[0-9a-f]{6}$/i.test(value.trim())

let current: HTMLElement | null = null

export function closeColorPicker() {
  current?.remove()
  current = null
}

export function openColorPicker(anchor: HTMLElement, start: string, options: Options) {
  closeColorPicker()
  let hsv = hexToHsv(isHex(start) ? start : '#FF5A1F')
  const pop = document.createElement('div')
  pop.className = 'cp-pop'
  pop.setAttribute('role', 'dialog')
  pop.setAttribute('aria-label', 'Pick a colour')
  pop.innerHTML = `
    <div class="cp-sv" tabindex="0" aria-label="Saturation and brightness"><span class="cp-thumb"></span></div>
    <div class="cp-hue" tabindex="0" aria-label="Hue"><span class="cp-thumb"></span></div>
    <div class="cp-row">
      <span class="cp-preview"></span>
      <input class="input cp-hex" maxlength="7" spellcheck="false" aria-label="Hex colour">
      <button type="button" class="btn btn-primary btn-sm cp-use">Use</button>
    </div>`
  document.body.append(pop)
  current = pop
  const sv = pop.querySelector<HTMLElement>('.cp-sv')!
  const hue = pop.querySelector<HTMLElement>('.cp-hue')!
  const hexInput = pop.querySelector<HTMLInputElement>('.cp-hex')!
  const preview = pop.querySelector<HTMLElement>('.cp-preview')!

  const place = () => {
    const r = anchor.getBoundingClientRect()
    const top = r.bottom + 8 + window.scrollY
    const left = Math.min(window.scrollX + r.left, window.scrollX + document.documentElement.clientWidth - pop.offsetWidth - 12)
    pop.style.top = `${top}px`
    pop.style.left = `${Math.max(12, left)}px`
  }
  let last = 0
  const render = (emit: boolean) => {
    const hex = hsvToHex(hsv)
    sv.style.setProperty('--hue', hsvToHex({ h: hsv.h, s: 1, v: 1 }))
    sv.querySelector<HTMLElement>('.cp-thumb')!.style.cssText = `left:${hsv.s * 100}%;top:${(1 - hsv.v) * 100}%;background:${hex}`
    hue.querySelector<HTMLElement>('.cp-thumb')!.style.left = `${(hsv.h / 360) * 100}%`
    preview.style.background = hex
    if (document.activeElement !== hexInput) hexInput.value = hex
    // Throttled live preview while dragging.
    const now = performance.now()
    if (emit && options.onInput && now - last > 90) {
      last = now
      options.onInput(hex)
    }
  }
  const drag = (el: HTMLElement, move: (x: number, y: number) => void) => {
    const at = (e: PointerEvent) => {
      const r = el.getBoundingClientRect()
      move(Math.min(1, Math.max(0, (e.clientX - r.left) / r.width)), Math.min(1, Math.max(0, (e.clientY - r.top) / r.height)))
      render(true)
    }
    el.addEventListener('pointerdown', (e) => {
      el.setPointerCapture(e.pointerId)
      at(e)
      const up = () => {
        el.removeEventListener('pointermove', at)
        options.onInput?.(hsvToHex(hsv))
      }
      el.addEventListener('pointermove', at)
      el.addEventListener('pointerup', up, { once: true })
    })
  }
  drag(sv, (x, y) => (hsv = { ...hsv, s: x, v: 1 - y }))
  drag(hue, (x) => (hsv = { ...hsv, h: x * 359.9 }))
  sv.addEventListener('keydown', (e) => {
    const step = e.shiftKey ? 0.1 : 0.02
    if (e.key === 'ArrowLeft') hsv.s = Math.max(0, hsv.s - step)
    else if (e.key === 'ArrowRight') hsv.s = Math.min(1, hsv.s + step)
    else if (e.key === 'ArrowUp') hsv.v = Math.min(1, hsv.v + step)
    else if (e.key === 'ArrowDown') hsv.v = Math.max(0, hsv.v - step)
    else return
    e.preventDefault()
    render(true)
  })
  hue.addEventListener('keydown', (e) => {
    const step = e.shiftKey ? 15 : 3
    if (e.key === 'ArrowLeft') hsv.h = (hsv.h - step + 360) % 360
    else if (e.key === 'ArrowRight') hsv.h = (hsv.h + step) % 360
    else return
    e.preventDefault()
    render(true)
  })
  hexInput.addEventListener('input', () => {
    const value = hexInput.value.startsWith('#') ? hexInput.value : `#${hexInput.value}`
    if (isHex(value)) {
      hsv = hexToHsv(value)
      render(true)
    }
  })
  const pick = () => {
    options.onPick(hsvToHex(hsv))
    closeColorPicker()
    anchor.focus()
  }
  pop.querySelector('.cp-use')!.addEventListener('click', pick)
  hexInput.addEventListener('keydown', (e) => e.key === 'Enter' && (e.preventDefault(), pick()))
  pop.addEventListener('keydown', (e) => e.key === 'Escape' && (closeColorPicker(), anchor.focus()))
  setTimeout(() => document.addEventListener('pointerdown', function outside(e) {
    if (!current || current.contains(e.target as Node) || anchor.contains(e.target as Node)) return
    pick() // clicking away keeps the colour being previewed
    document.removeEventListener('pointerdown', outside)
  }))
  render(false)
  place()
  sv.focus()
}
